"use client";

import api from "@/lib/axios";
import {
    NormalizedPermissions,
    initialNormalizedPermissions,
    normalizePermissions
} from "@/lib/permissions/normalizer";
import { RootState } from "@/store/store";
import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { useSelector } from "react-redux";

interface PermissionContextType {
    permissions: NormalizedPermissions;
    isLoading: boolean;
    error: string | null;
    refreshPermissions: () => Promise<void>;
}

const PermissionContext = createContext<PermissionContextType>({
    permissions: initialNormalizedPermissions,
    isLoading: true,
    error: null,
    refreshPermissions: async () => { },
});

export function PermissionProvider({ children }: { children: React.ReactNode }) {
    const [permissions, setPermissions] = useState<NormalizedPermissions>(initialNormalizedPermissions);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // We need to know when user is logged in to fetch permissions
    const { user } = useSelector((state: RootState) => state.auth);

    const fetchPermissions = useCallback(async () => {
        if (!user) {
            setPermissions(initialNormalizedPermissions);
            setIsLoading(false);
            return;
        }

        try {
            setIsLoading(true);
            setError(null);

            const response = await api.get("/permissions/my-permissions", { service: 'auth' } as any);
            const data = response.data?.data || {};

            const normalized = normalizePermissions(data);
            setPermissions(normalized);

        } catch (err: any) {
            console.error("Failed to load permissions:", err);
            setError("Failed to load permissions. Some features may be unavailable.");
        } finally {
            setIsLoading(false);
        }
    }, [user]);

    // Fetch on mount or user change
    useEffect(() => {
        fetchPermissions();
    }, [fetchPermissions]);

    useEffect(() => {
        if (!isLoading) {
            console.log("Normalized UI Permission Model:", permissions);
        }
    }, [permissions, isLoading]);

    return (
        <PermissionContext.Provider value={{ permissions, isLoading, error, refreshPermissions: fetchPermissions }}>
            {children}
        </PermissionContext.Provider>
    );
}

export function usePermissionContext() {
    const context = useContext(PermissionContext);
    if (!context) {
        throw new Error("usePermissionContext must be used within a PermissionProvider");
    }
    return context;
}
